/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import { CSSProperties, forwardRef, useRef, useImperativeHandle, useMemo } from "react";

import { cva, cx } from "@panda/css";

import { CosmosClickableBox } from "../../";

import type { RecipeVariantProps } from "@panda/css";
import { useBackdropFilterFix, useShouldReduceBlur } from "@components/hooks";

const cardStyles = cva({
	base: {
		zIndex: "2",
		display: "flex",
		gap: "eds.dimension.16",
		transition: "background token(animations.eds.duration.200) token(animations.eds.easing.easeInOutQuad)",

		"--cosmos-card__thumbnail-radius": "calc(var(--cosmos-card-radius) - var(--cosmos-card-padding))",
		borderRadius: "var(--cosmos-card-radius, 0)",
		padding: "var(--cosmos-card-padding, 0)",
		"& .cosmos-card__link::before": {
			borderRadius: "var(--cosmos-card-radius, 0)",
		},

		"&:has(.cosmos-card__link[href]:focus-visible)": {
			outline: "token(spacing.eds.dimension.4) solid #fff",
			outlineOffset: "eds.dimension.4",
		},

		"&:has(.cosmos-card__link[href]:hover), &:has(.cosmos-card__link[href]:focus-visible)": {
			"& .cosmos-card__thumbnail img, & .cosmos-card__thumbnail video": {
				transform: "scale(1.05)",
			},
		},

		"& .cosmos-card__content": {
			paddingBlockStart: "0",
		},
	},
	variants: {
		borderRadius: {
			0: {
				"--cosmos-card-radius": "0",
			},
			8: {
				"--cosmos-card-radius": "token(radii.eds.borderRadius.8)",
			},
			12: {
				"--cosmos-card-radius": "token(radii.eds.borderRadius.12)",
			},
			16: {
				"--cosmos-card-radius": "token(radii.eds.borderRadius.16)",
			},
			24: {
				"--cosmos-card-radius": "token(radii.eds.borderRadius.24)",
			},
		},
		padding: {
			0: {
				"--cosmos-card-padding": "0",
			},
			8: {
				"--cosmos-card-padding": "token(spacing.eds.dimension.8)",
			},
			12: {
				"--cosmos-card-padding": "token(spacing.eds.dimension.12)",
			},
			16: {
				"--cosmos-card-padding": "token(spacing.eds.dimension.16)",
			},
			20: {
				"--cosmos-card-padding": "token(spacing.eds.dimension.20)",
			},
			24: {
				"--cosmos-card-padding": "token(spacing.eds.dimension.24)",
			},
			32: {
				"--cosmos-card-padding": "token(spacing.eds.dimension.32)",
				smDown: {
					"--cosmos-card-padding": "token(spacing.eds.dimension.24)",
				},
			},
		},
		variant: {
			ghost: {},
			solid: {
				backgroundColor: "eds.background.elevated.high.default",

				"&:has(.cosmos-card__link[href]:hover)": {
					backgroundColor: "eds.background.elevated.high.hover",
				},
			},
			material: {
				border: "token(spacing.eds.dimension.1) solid token(colors.eds.palette.transparent.light.005)",
				backgroundColor: "eds.palette.transparent.light.005",
				backdropFilter: "var(--effect-material-blur, blur(50px))",

				"&:has(.cosmos-card__link[href]:hover)": {
					backgroundColor: "eds.palette.transparent.light.010",
				},
			},
			materialLight: {
				border: "token(spacing.eds.dimension.1) solid token(colors.eds.palette.transparent.light.010)",
				backgroundColor: "eds.palette.transparent.light.010",
				backdropFilter: "var(--effect-material-blur, blur(50px))",

				"&:has(.cosmos-card__link[href]:hover)": {
					backgroundColor: "eds.palette.transparent.light.015",
				},
			},
			materialDark: {
				border: "token(spacing.eds.dimension.1) solid token(colors.eds.palette.transparent.light.010)",
				backgroundColor: "eds.palette.transparent.dark.015",
				backdropFilter: "var(--effect-material-blur, blur(50px))",

				"&:has(.cosmos-card__link[href]:hover)": {
					backgroundColor: "eds.palette.transparent.dark.010",
				},
			},
			outlined: {
				border: "token(spacing.eds.dimension.2) solid token(colors.eds.border.subtle)",
			},
			outlinedThin: {
				border: "token(spacing.eds.dimension.1) solid token(colors.eds.border.subtle)",
			},
		},
		direction: {
			row: {
				flexDirection: "row",
				"& .cosmos-card__content": {
					paddingBlockStart: "eds.dimension.16",
					paddingInlineStart: "0",
				},
				'&[data-variant="ghost"] .cosmos-card__content': {
					paddingBlockStart: "0",
				},
			},
			mdRow: {
				"&&": {
					flexDirection: "column",
					md: {
						flexDirection: "row",
						"& .cosmos-card__content": {
							paddingBlockStart: "eds.dimension.16",
							paddingInlineStart: "0",
						},
						'&[data-variant="ghost"] .cosmos-card__content': {
							paddingBlockStart: "0",
						},
					},
				},
			},
			mdRowReverse: {
				"&&": {
					flexDirection: "column",
					md: {
						flexDirection: "row-reverse",
					},
				},
			},
			geminiDir: {
				base: {
					flexDirection: "row-reverse",
					"& .cosmos-card__content": {
						padding: "eds.dimension.8",
					},
				},
				mdOnly: {
					flexDirection: "column-reverse",
				},
				smOnly: {
					flexDirection: "column-reverse",
				},
				xsDown: {
					flexDirection: "column-reverse",
				},
			},
			delphinusDir: {
				base: {
					flexDirection: "row-reverse",
					"& .cosmos-card__content": {
						padding: "eds.dimension.8",
					},
				},
				smDown: {
					flexDirection: "column-reverse",
				},
			},
			column: {
				flexDirection: "column",
			},
		},
		assetPosition: {
			start: {
				flexDirection: "row-reverse",
			},
			end: {
				flexDirection: "row",
			},
		},
	},
	compoundVariants: [
		{
			borderRadius: 12,
			variant: "outlined",
			css: {
				"--cosmos-card-radius": "token(spacing.eds.dimension.12)",
				"--cosmos-card__thumbnail-radius": "calc(token(spacing.eds.dimension.12) - token(spacing.eds.dimension.4))",
			},
		},
		{
			borderRadius: 16,
			variant: "outlined",
			css: {
				"--cosmos-card-radius": "token(spacing.eds.dimension.16)",
				"--cosmos-card__thumbnail-radius": "calc(token(spacing.eds.dimension.16) - token(spacing.eds.dimension.4))",
			},
		},
	],
});

export type CosmosCardProps = RecipeVariantProps<typeof cardStyles> & {
	children?: React.ReactNode;
	className?: string;
	style?: CSSProperties;
};

export const CosmosCard = forwardRef(
	(
		{
			// cardRadius - cardPadding = thumbnailRadius
			// so if neither of these are passed with different values, then thumbnailRadius will be 0
			borderRadius = 24,
			padding = 24,
			children,
			className,
			variant = "ghost",
			direction = "column",
			assetPosition,
			style,
			...props
		}: CosmosCardProps,
		forwardedRef: React.Ref<HTMLDivElement>,
	): React.JSX.Element => {
		const internalRef = useRef<HTMLDivElement>(null);

		useBackdropFilterFix(variant.startsWith("material") ? internalRef : undefined);
		useImperativeHandle(forwardedRef, () => internalRef.current!, []);

		const shouldReduceBlur = useShouldReduceBlur();
		const isMaterialVariant = variant.startsWith("material");

		const optimizedStyle = useMemo((): CSSProperties => {
			if (!isMaterialVariant) {
				return style ?? {};
			}

			const blurValue = shouldReduceBlur ? "blur(12px)" : "blur(50px)";

			return {
				...style,
				"--effect-material-blur": blurValue,
			} as CSSProperties;
		}, [isMaterialVariant, shouldReduceBlur, style]);

		return (
			<CosmosClickableBox
				{...props}
				className={cx(cardStyles({ borderRadius, padding, variant, direction, assetPosition }), className)}
				data-variant={variant}
				data-direction={direction}
				ref={internalRef}
				style={optimizedStyle}
			>
				{children}
			</CosmosClickableBox>
		);
	},
);

CosmosCard.displayName = "CosmosCard";
